
import PaymentMethod from "@/models/payment-method/PaymentMethod";
import Vue from "vue";
import { Component, PropSync, Watch } from "vue-property-decorator";
import { SetupIntentType } from "@/models/subscription/SetupIntentTypes";
import PaymentMethodChangeDisclaimer from "@/components/payment-method/PaymentMethodChangeDisclaimer.vue";
import BaseView from "@/views/BaseView.vue";
import SubscriptionPlan from "@/models/subscription/SubscriptionPlan";

@Component({
  components: {
    PaymentMethodChangeDisclaimer,
    No2FAPlaceholder: () =>
      import("@/views/main-legacy/wallet/components/No2FAPlaceholder.vue"),
  },
})
export default class ChangePaymentMethodDialog extends BaseView {
  // 🚩 Flag which determines whether the dialog should be shown
  @PropSync("showDialog", { type: Boolean })
  public show!: boolean;

  // 📦 Stripe instance
  private stripe = (window as any).Stripe(process.env.VUE_APP_STRIPE_KEY);

  // 📦 Stripe visual elements instance
  private elements: any = this.stripe.elements();

  // 📦 Payment form card that is displayed for the user
  private card: any;

  // 📦⚠ Error holder variable for the Stripe card element
  private cardError: string | null = null;

  // 📦 Ideal form card
  private idealForm: any;

  // The selected payment method
  private selectedPaymentMethod: number = -1;

  // 🏗 Configuration for the payment card
  private cardConfig = {
    hidePostalCode: true,
    iconStyle: "default",
  };

  // 🎨 Style properties that should be applied on the card
  private cardStyle = {
    base: {
      color: this.$vuetify.theme.dark
        ? this.$vuetify.theme.currentTheme.secondary
        : "#000000",
      fontSize: "16px",
      fontSmoothing: "antialiased",
      fontWeight: "500",
      "::placeholder": {
        color: this.$vuetify.theme.currentTheme.secondary,
      },
    },

    complete: {
      color: "#00cb06",
      iconColor: "#00cb06",
      ":-webkit-autofill": { color: "#00cb06" },
    },
    invalid: {
      color: "#ff0000",
      iconColor: "#ff0000",
      ":-webkit-autofill": { color: "#ff0000" },
    },
  };

  // 🎨 Style for different states of the card
  private cardOptions = {
    style: this.cardStyle,
    hidePostalCode: true,
    iconStyle: "default",
    classes: {
      base: "form-stripe-element",
      focus: "form-stripe-element--focus",
      complete: "form-stripe-element--is-success",
      invalid: "form-stripe-element--is-error",
      empty: "form-stripe-element",
    },
  };

  // 📦 The current user's fullname
  private customerName: string | null = null;

  // 🚩 Flag for whether API calls are being executed
  private isLoading: boolean = false;

  // 🚩 Flag for whether the credit card form is correctly filled
  private isCreditCardFormValid: boolean = false;

  // 🚩 Flag for whether the iDeal form correctly filled
  private isIDealFormValid: boolean = false;

  // 🚩 Flag for whether the provided customer name is valid
  private isCustomerNameValid: boolean = false;

  // 📦 The email of the current user
  private get customerEmail(): string {
    return this.$store.state.userData.email;
  }

  // 📦 The payment method of the current user
  private get currentPaymentMethod(): PaymentMethod {
    return this.$store.state.payments.currentPaymentMethod;
  }

  // 📦 The current subscription plan of the user
  private get currentSubscriptionPlan(): SubscriptionPlan {
    return this.$store.state.subscription.currentSubscriptionPlan;
  }

  /**
   * 👂 Listener for changes in the provided customer name
   */
  @Watch("customerName")
  private onCustomerNameChanged(val: string, oldVal?: string) {
    this.isCustomerNameValid = val !== "" && val !== null;
  }

  /**
   * 👂 Listener for changes in the selected payment method
   */
  @Watch("selectedPaymentMethod")
  private onPaymentMethodChanged(val: number, oldVal: number) {
    switch (val) {
      case 0:
        this.createCreditCardForm();
        break;
      case 1:
        this.createIdealCardForm();
        break;
    }
  }

  /**
   * 🏗 Builds the credit card form
   */
  private createCreditCardForm() {
    if (this.card != null) {
      return;
    }

    // Create and mount the payment card
    this.card = this.elements.create("card", {
      ...this.cardOptions,
      ...this.cardStyle,
    });
    this.card.mount(this.$refs.card);

    // 👂 Listen for changes in the credit card form
    this.card.addEventListener("change", (event: any) => {
      this.isCreditCardFormValid =
        event.error === undefined && !event.empty && event.complete;

      this.cardError = event.error ? event.error.message : null;
    });
  }

  /**
   * 🏗 Builds the ideal card form
   */
  private createIdealCardForm() {
    if (this.idealForm != null) {
      return;
    }

    this.idealForm = this.elements.create("idealBank", {
      ...this.cardOptions,
      ...this.cardStyle,
    });
    this.idealForm.mount(this.$refs.idealForm);

    // 👂 Listen for changes in the deal form
    this.idealForm.addEventListener("change", (event: any) => {
      this.isIDealFormValid =
        event.error === undefined && !event.empty && event.complete;
    });
  }

  private async getSetupIntent(intentType: SetupIntentType) {
    this.isLoading = true;
    Vue.prototype.$api
      .get("/api/v1/paymentsetup", {
        payment_method_type: intentType,
      })
      .then((response: any) => {
        switch (intentType) {
          case SetupIntentType.Card: {
            this.confirmCardPayment(response.data);
            break;
          }
          case SetupIntentType.IDeal: {
            this.confirmIdealPayment(response.data);
            break;
          }
        }
      });
  }

  /**
   * 🚀 Registers a new card payment method on Stripe
   */
  private confirmCardPayment(clientSecret: string) {
    this.stripe
      .confirmCardSetup(clientSecret, {
        payment_method: {
          card: this.card,
          billing_details: {
            name: this.customerName,
            email: this.customerEmail,
          },
        },
        return_url: window.location.origin + this.$route.path + "?mode=change",
      })
      .then((result: any) => {
        if (result.error) {
          this.showErrorSnackbar(result.error.message);
          this.isLoading = false;
        } else {
          this.updatePaymentMethod(SetupIntentType.Card);
        }
      });
  }

  /**
   * 🚀 Registers a new IDeal payment method on Stripe
   */
  private confirmIdealPayment(clientSecret: string) {
    this.stripe.confirmIdealSetup(clientSecret, {
      payment_method: {
        ideal: this.idealForm,
        billing_details: {
          name: this.customerName,
          email: this.customerEmail,
        },
      },
      return_url: window.location.origin + this.$route.path + "?mode=change",
    });
  }

  private async updatePaymentMethod(type: SetupIntentType) {
    this.isLoading = true;
    let request =
      type === SetupIntentType.OBT
        ? Vue.prototype.$api.put("/api/v1/paymentsubscriptions", {
            use_obt: true,
          })
        : Vue.prototype.$api.post("/api/v1/paymentmethods", {
            type: type,
          });

    request
      .then((response: any) => {
        this.showSuccessSnackbar(
          "Your billing method was successfully changed!"
        );
        this.$store.dispatch("getCurrentPaymentMethod");
        this.show = false;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
